"use client";

import Image from "next/image";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Link } from "@/i18n/navigation";
import { useState, useEffect } from "react";
import { motion } from "motion/react";
import LanguagePicker from "../LanguagePicker";
import { useTranslations } from "next-intl";
import { EcosystemHover } from "../EcosystemHover";
import { useWindowSize } from "@/hooks/common/useWindowSize";

interface SubNav {
  title: string;
  href: string;
  target?: "_blank";
  rel?: "noopener noreferrer";
  isExternal?: boolean;
}

interface NavItem {
  title?: string;
  href?: string;
  isLogo?: boolean;
  target?: "_blank";
  rel?: "noopener noreferrer";
  isExternal?: boolean;
  subNavs?: SubNav[];
}

const Header = () => {
  const t = useTranslations("layout");
  const [open, setOpen] = useState(false);
  const { width } = useWindowSize();
  const isDesktop = width >= 1028;
  const [openSub, setOpenSub] = useState<number | null>(null);

  const toggleSub = (idx: number) => {
    setOpenSub(prev => (prev === idx ? null : idx));
  };

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setOpenSub(null);
    }
  }, [open]);

  const navs: NavItem[] = [
    {
      title: t("user"),
      href: "/user",
      subNavs: [
        {
          title: t("for_user"),
          href: "/user"
        },
        {
          title: t("community"),
          href: "/community",
        },
        {
          title: t("become-our-ambassador"),
          href: "/become-hyrasapien",
          target: "_blank",
          rel: "noopener noreferrer",
          isExternal: true,
        },
      ],
    },
    {
      title: t("developer"),
      href: "/developer",
      subNavs: [
        {
          title: t("for_developers"),
          href: "/developer",
        },
        {
          title: t("governance"),
          href: "/governance",
        },
      ],
    },
    {
      title: t("enterprise"),
      href: "/enterprise",
      subNavs: [
        {
          title: t("for_enterprises"),
          href: "/enterprise",
        },
        {
          title: t("for_investors"),
          href: "/enterprise/investor",
        },
      ],
    },
    {
      title: t("about-us"),
      href: "/about-us",
      subNavs: [
        {
          title: t("about_us_1"),
          href: "/about-us",
        },
        {
          title: t("roadmap"),
          href: "/roadmap",
        },
      ],
    },
    { isLogo: true },
    { title: t("ecosystem"), href: "/ecosystem" },
    {
      title: t("resources"),
      href: "#",
      subNavs: [
        {
          title: t("docs-sdk"),
          href: "https://docs.hyra.network/docs/overview/",
          target: "_blank",
          rel: "noopener noreferrer",
          isExternal: true,
        },
        {
          title: "SDK",
          href: "https://sdk.hyra.network/",
          target: "_blank",
          rel: "noopener noreferrer",
          isExternal: true,
        },
        {
          title: "White paper",
          href: "https://whitepaper.hyra.network?v=1.0",
          target: "_blank",
          rel: "noopener noreferrer",
          isExternal: true,
        },
        {
          title: "Media Kit",
          href: "/media-kit",
        },
        { title: t("q-a"), href: "/faq" },
      ],
    },
    { title: t("news"), href: "/news" },
    { title: "Lambo ball", href: "https://lamboballs.hyra.network/" },
  ];

  return (
    <motion.header
      id="site-header"
      initial={{ y: -24, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      viewport={{ once: true }}
      className="fixed inset-x-0 top-0 z-50 bg-white"
    >
      <div className="mx-auto flex h-[60px] w-full max-w-[1440px] items-center justify-between gap-4 px-4 md:h-20 md:px-10">
        {!isDesktop && (
          <Link href="/">
            <div className="relative aspect-[2.5] h-[40px] w-auto">
              <Image
                src="/images/icons/logo-hyra-network.svg"
                alt="logo"
                priority
                fill
              />
            </div>
          </Link>
        )}

        {/* Desktop Navigation - Full Display */}
        {isDesktop && (
          <nav className="flex w-full flex-1 items-center justify-center">
            {navs.map((nav, index) => {
              // Render logo ở giữa
              if (nav.isLogo) {
                return (
                  <Link key="logo-center" href="/" className="mx-[80px]">
                    <div className="relative aspect-[2.5] h-[40px] w-auto">
                      <Image
                        src="/images/icons/logo-hyra-network.svg"
                        alt="logo"
                        priority
                        fill
                      />
                    </div>
                  </Link>
                );
              }

              if (nav.title === t("ecosystem")) {
                return (
                  <div
                    key={nav.title}
                    className="group relative flex items-center"
                  >
                    <EcosystemHover
                      trigger={
                        <Link
                          href={nav.href || "/"}
                          className="flex items-center gap-1 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50"
                        >
                          <span className="whitespace-nowrap text-sm font-medium uppercase text-black">
                            {nav.title}
                          </span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 17 16"
                            fill="none"
                            className="shrink-0 rotate-90"
                          >
                            <path
                              d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12"
                              stroke="#282930"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </Link>
                      }
                    />
                  </div>
                );
              }

      const hasSub = Array.isArray(nav.subNavs) && nav.subNavs.length > 0;
      // Chỉ vô hiệu click cho 4 mục đầu có submenu trên DESKTOP
      const disableTopLevel = hasSub && index < 4;

          if (hasSub) {
                return (
                  <div key={nav.title} className="group relative">
                  {disableTopLevel ? (
                    // KHÔNG điều hướng: dùng button để giữ style nhưng không href
                    <button
                      type="button"
                      aria-haspopup="true"
                      aria-expanded="false"
                      className="flex items-center gap-1 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50 cursor-default"
                      // Không làm gì khi bấm
                      onClick={(e) => e.preventDefault()}
                    >
                      <span className="whitespace-nowrap text-sm font-medium uppercase text-black">
                        {nav.title}
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 17 16" fill="none" className="shrink-0 rotate-90">
                        <path d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12" stroke="#282930" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  ) : (
                    // VẪN điều hướng bình thường cho các mục còn lại
                    <Link
                      href={nav.href || "/"}
                      className="flex items-center gap-1 rounded-lg px-3 py-2 transition-colors hover:bg-gray-50"
                    >
                      <span className="whitespace-nowrap text-sm font-medium uppercase text-black">
                        {nav.title}
                      </span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 17 16"
                        fill="none"
                        className="shrink-0 rotate-90"
                      >
                        <path
                          d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12"
                          stroke="#282930"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    </Link>
                  )}

                    {/* Dropdown submenu on hover */}
                    <div className="invisible absolute left-0 top-full pt-2 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                      <div className="min-w-[160px] rounded-lg border border-gray-100 bg-[#F9F9F9] py-2 shadow-lg">
                        {nav.subNavs!.map((subNav) => {
                          if (subNav.isExternal) {
                            return (
                              <a
                                key={subNav.title}
                                href={subNav.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block px-4 py-2 text-sm text-gray-700 transition-colors whitespace-nowrap hover:bg-gray-100 hover:text-black"
                              >
                                {subNav.title}
                              </a>
                            );
                          }
                          return (
                            <Link
                              key={subNav.title}
                              href={subNav.href}
                              className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-black"
                            >
                              {subNav.title}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <Link
                  key={nav.title}
                  href={nav.href || "/"}
                  className="flex items-center rounded-lg px-3 py-2 transition-colors hover:bg-gray-50"
                >
                  <span className="whitespace-nowrap text-sm font-medium uppercase text-black">
                    {nav.title}
                  </span>
                </Link>
              );
            })}
          </nav>
        )}

        {/* Right side controls */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-4 lg:gap-1">
            <p className="hidden items-center justify-center text-2xl text-[#202020] xl:flex">
              |
            </p>
            <LanguagePicker />

            {/* Mobile Menu Button */}
            {!isDesktop && (
              <>
                <button onClick={() => setOpen(true)}>
                  <Image
                    src="/images/icons/bars.svg"
                    alt="bars"
                    width={24}
                    height={24}
                    priority
                  />
                </button>

                {open && (
                  <div className="fixed inset-0 z-50 flex h-dvh min-h-0 flex-col bg-white">
                    <div className="flex justify-between p-4 pt-2.5">
                      <Link href="/">
                        <div className="relative aspect-[2.5] h-[40px] w-auto">
                          <Image
                            src="/images/icons/logo-hyra-network.svg"
                            alt="logo"
                            priority
                            fill
                          />
                        </div>
                      </Link>
                      <button
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                        className="text-xl"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto overscroll-y-contain px-6">
                      {navs.map((item, index) => {
                        if (item.isLogo) return null;
                        const hasSub = Array.isArray(item.subNavs) && item.subNavs.length > 0;
                        const isOpen = openSub === index;

                        return (
                          <div key={index} className="flex flex-col">
                          <div className="flex w-full items-center justify-between py-2">
                              {hasSub ? (
                                // KHÔNG điều hướng trên mobile: bấm title để mở submenu
                                <button
                                  type="button"
                                  aria-expanded={isOpen}
                                  aria-controls={`sub-${index}`}
                                  onClick={() => toggleSub(index)}
                                  className="text-left text-lg font-medium text-black flex-1 pr-3"
                                >
                                  {item.title}
                                </button>
                              ) : item.isExternal ? (
                              <a
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setOpen(false)}
                                className="text-lg font-medium text-black flex-1 pr-3"
                              >
                                  {item.title}
                                </a>
                            ) : (
                              <Link
                                href={item.href || "/"}
                                onClick={() => setOpen(false)}
                                className="text-lg font-medium text-black flex-1 pr-3"
                              >
                                  {item.title}
                            </Link>
                          )}

                          {/* Chevron button toggles submenu (if any) */}
                          {hasSub ? (
                              <button
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={`sub-${index}`}
                                onClick={() => toggleSub(index)}
                                className="grid place-items-center rounded-md p-1.5 transition hover:bg-neutral-100 active:scale-95"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="17"
                                  height="16"
                                  viewBox="0 0 17 16"
                                  fill="none"
                                  className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-90" : "-rotate-90"}`}
                                >
                                  <path
                                    d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12"
                                    stroke="black"
                                    strokeOpacity="0.6078"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            ) : null}
                        </div>

                        {/* Collapsible submenu */}
                        {hasSub && (
                          <div
                            id={`sub-${index}`}
                            className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
                              isOpen
                                ? "max-h-96 opacity-100"
                                : "max-h-0 opacity-0"
                            }`}
                          >
                            <div className="flex flex-col gap-1 pb-2">
                              {item.subNavs!.map((sub, subIndex) => {
                              if (sub.isExternal) {
                                return (
                                  <div
                                    key={subIndex}
                                    className="flex items-center justify-start gap-2"
                                  >
                                    <a
                                      href={sub.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() => setOpen(false)}
                                      className="py-1 pl-4 text-sm text-neutral-600 hover:text-neutral-900"
                                    >
                                      {sub.title}
                                    </a>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="17"
                                      height="16"
                                      viewBox="0 0 17 16"
                                      fill="none"
                                      className="shrink-0"
                                    >
                                      <path
                                        d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12"
                                        stroke="black"
                                        strokeOpacity="0.6078"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={subIndex}
                                  className="flex items-center justify-start gap-2"
                                >
                                  <Link
                                    href={sub.href}
                                    onClick={() => setOpen(false)}
                                    className="py-2 pl-4 text-sm text-neutral-600 hover:text-neutral-900"
                                  >
                                    {sub.title}
                                  </Link>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="17"
                                    height="16"
                                    viewBox="0 0 17 16"
                                    fill="none"
                                    className="shrink-0"
                                  >
                                    <path
                                      d="M6.5 4L10.4604 7.90572C10.5132 7.95779 10.5132 8.04221 10.4604 8.09428L6.5 12"
                                      stroke="black"
                                      strokeOpacity="0.6078"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
